import { defineMessages } from 'react-intl';

import { LoadLevel } from 'protocol/model/dto_system_load_pb';

import { SourceKey } from './types';

export const sourceLabels = defineMessages<SourceKey>({
  UploadKafka: {
    id: 'systemLoad.source.uploadKafka',
    defaultMessage: 'Upload processing (Kafka lag)',
  },
  UploadCBCT: {
    id: 'systemLoad.source.uploadCBCT',
    defaultMessage: 'CBCT conversion queue (Redis)',
  },
  PipelineOrgPending: {
    id: 'systemLoad.source.pipelineOrgPending',
    defaultMessage: 'Pipeline: queue position in organization',
  },
  PipelineOrgsCount: {
    id: 'systemLoad.source.pipelineOrgsCount',
    defaultMessage: 'Pipeline: organizations in queue',
  },
});

export const levelLabels = defineMessages<LoadLevel>({
  [LoadLevel.LoadLevel_InvalidValue]: {
    id: 'systemLoad.level.unset',
    defaultMessage: 'Unset',
  },
  [LoadLevel.LoadLevel_Low]: {
    id: 'systemLoad.level.low',
    defaultMessage: 'Low',
  },
  [LoadLevel.LoadLevel_Medium]: {
    id: 'systemLoad.level.medium',
    defaultMessage: 'Medium',
  },
  [LoadLevel.LoadLevel_High]: {
    id: 'systemLoad.level.high',
    defaultMessage: 'High',
  },
});

export const columnTooltips = defineMessages({
  source: {
    id: 'systemLoad.tooltip.source',
    defaultMessage:
      'Which backend queue is measured: uploaded files processing (Kafka consumer lag), CBCT DICOM to DCCT conversion (Redis queue), or the report pipeline. The pipeline is fair-queued across organizations, so it has two metrics: a report’s queue position within its own organization, and the number of organizations with pending reports. A report’s banner uses the worst of the two pipeline metrics.',
  },
  rawValue: {
    id: 'systemLoad.tooltip.rawValue',
    defaultMessage:
      'Current raw metric: consumer lag / pending conversions / organizations with pending reports. N/A when the metric is evaluated per report (queue position in organization). Upload metrics are refreshed by a background worker every ~15 seconds.',
  },
  computed: {
    id: 'systemLoad.tooltip.computed',
    defaultMessage:
      'Level derived automatically from the raw value and the thresholds. Falls back to Low when the metrics are stale. N/A for per-report metrics — they are computed per report on the patient page.',
  },
  effective: {
    id: 'systemLoad.tooltip.effective',
    defaultMessage:
      'What users actually get: the manual override when set, otherwise the computed level. This drives the high-load notice on the patient page cards.',
  },
  manual: {
    id: 'systemLoad.tooltip.manual',
    defaultMessage:
      'Forces the effective level regardless of measurements. Unset: use the computed level. High: show the notice even when the system is idle. Low: suppress the notice under real load.',
  },
  thresholds: {
    id: 'systemLoad.tooltip.thresholds',
    defaultMessage:
      'Map the raw value to a level: value >= High is high, value >= Medium is medium, otherwise low. Must satisfy 0 <= Medium <= High. Applied immediately after saving.',
  },
  enabled: {
    id: 'systemLoad.tooltip.enabled',
    defaultMessage:
      'Serve this metric to users. When disabled, it contributes nothing to the patient page notice (existing notices clear within a tick) and even a manual override is ignored. Measurement continues, so the raw and computed values stay visible here.',
  },
});
